import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { logRelayMigrationDiagnostic } from './relayDiagnostics.ts';
import { getRelayOperationKey, type RelayOperationVariables } from './relayOperation.ts';

let disableRelayOverride: boolean | undefined;
let relayInteropEnabledOverride: boolean | undefined;
let relayMigrationDiagnosticsEnabled: boolean | undefined;

export function __setRelayInteropEnabledOverrideForFixtures(value: boolean | undefined): void {
	relayInteropEnabledOverride = value;
}

export function __TEST_ONLY_resetDisableRelayOverride(): void {
	disableRelayOverride = undefined;
}

export function __TEST_ONLY_resetRelayEnablementCaches(): void {
	relayMigrationDiagnosticsEnabled = undefined;
}

type RelayDecisionPhase =
	| 'preload'
	| 'component'
	| 'lazy'
	| 'imperative'
	| 'cache'
	| 'mutation'
	| 'fragment'
	| 'subscription'
	| 'memo'
	| 'unknown';

export type RelayEnablementOptions = {
	operationName?: string | null;
	operationKey?: string | null;
	variables?: RelayOperationVariables;
	phase?: RelayDecisionPhase;
};

function isRelayDisabledByQueryParam(): boolean {
	if (disableRelayOverride !== undefined) {
		return disableRelayOverride;
	}

	try {
		if (typeof window !== 'undefined' && window.location?.search) {
			const params = new URLSearchParams(window.location.search);
			const value = params.get('disableRelay');
			disableRelayOverride = value === '1' || value === 'true';
		} else {
			disableRelayOverride = false;
		}
	} catch {
		disableRelayOverride = false;
	}

	return disableRelayOverride;
}

function isRelayMigrationDiagnosticsEnabled(): boolean {
	if (relayMigrationDiagnosticsEnabled === undefined) {
		relayMigrationDiagnosticsEnabled = Boolean(
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('confluence_relay_migration_diagnostics_enabled'),
		);
	}

	return relayMigrationDiagnosticsEnabled;
}

function logRelayDecision({
	callerPackage,
	operationName,
	operationKey,
	phase,
	relayEnabled,
	decisionReason,
}: {
	callerPackage?: string;
	operationName?: string | null;
	operationKey?: string | null;
	phase: RelayDecisionPhase;
	relayEnabled: boolean;
	decisionReason: string;
}): void {
	if (!isRelayMigrationDiagnosticsEnabled()) {
		return;
	}

	logRelayMigrationDiagnostic('Decision', {
		phase,
		callerPackage,
		operationName,
		operationKey,
		relayEnabled,
		decisionReason,
	});
}

export function isRelayEnabled(
	callerPackage?: string,
	options: RelayEnablementOptions = {},
): boolean {
	const phase = options.phase ?? 'unknown';
	const operationName = options.operationName ?? null;
	const operationKey =
		options.operationKey ?? getRelayOperationKey(operationName, options.variables) ?? null;

	// Query param override: ?disableRelay=1 or ?disableRelay=true forces Relay off
	if (isRelayDisabledByQueryParam()) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: false,
			decisionReason: 'disableRelay query param',
		});
		return false;
	}

	if (relayInteropEnabledOverride !== undefined) {
		logRelayDecision({
			callerPackage,
			operationName,
			operationKey,
			phase,
			relayEnabled: relayInteropEnabledOverride,
			decisionReason: 'relay interop override',
		});
		return relayInteropEnabledOverride;
	}

	const relayEnabled = !StatsigConfigurations.getBoolean(
		'confluence_relay_rollout_force_apollo',
		false,
	);
	logRelayDecision({
		callerPackage,
		operationName,
		operationKey,
		phase,
		relayEnabled,
		decisionReason: relayEnabled ? 'Relay default' : 'Apollo fallback config',
	});
	return relayEnabled;
}
